import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/format-api-error";
import type { ContentAssetRow } from "./types";
import { useContentFolders } from "./use-content-folders";
import { useMoveAsset } from "./use-move-asset";

// Phase 7 sub-bucket 13. SPA port of legacy MoveAssetDialog. Flat folder
// picker — Colab has ~10 active folders; a tree picker doesn't earn its
// cost here. The destination select shows the current parent (if any) as
// the initial value; selecting Root unsets `parent_asset_id`.
export function MoveAssetDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: ContentAssetRow;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const folders = useContentFolders();
  const move = useMoveAsset();
  const [target, setTarget] = useState<string>(asset.parent_asset_id ?? "");

  const otherFolders = (folders.data ?? []).filter((f) => f.id !== asset.id);

  function handleMove() {
    move.mutate(
      { id: asset.id, parentAssetId: target || null },
      {
        onSuccess: () => {
          toast.success(target ? "Moved into folder" : "Moved to root");
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to move"));
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (move.isPending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Move {asset.title}
          </DialogTitle>
          <DialogDescription>
            Pick a destination folder, or choose Root to move it out of any folder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="target">Destination</Label>
          <select
            id="target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
            disabled={folders.isLoading}
          >
            <option value="">Root (no folder)</option>
            {otherFolders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.title}
              </option>
            ))}
          </select>
          {folders.data && otherFolders.length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              No other folders yet. Create one from the Add asset menu.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={move.isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleMove} disabled={move.isPending}>
            {move.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
